import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { axiosInstance } from "../../config";
import Topbar from "../../components/topbar/Topbar";
import Leftbar from "../../components/LeftBar/lefbar";
import EditScheduledPostModal from "../../components/EditScheduledPostModal/EditScheduledPostModal";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";
import toast from "react-hot-toast";
import "./calendarPage.css";

export function CalendarView() {
  const { t } = useTranslation('schedule');
  const location = useLocation();

  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedPost, setSelectedPost] = useState(null);
  const [postToCancel, setPostToCancel] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null); // For mobile day detail view

  const DAY_KEYS = [
    t('sun'), t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat')
  ];

  const fetchScheduledPosts = useCallback(async () => {
    try {
      const startDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const endDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

      const res = await axiosInstance.get("/scheduled-posts", {
        params: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        }
      });
      setScheduledPosts(res.data.posts || []);
    } catch (err) {
      console.error("Failed to fetch scheduled posts:", err);
    }
  }, [currentMonth]);

  // Refetch when navigating to this page or when month changes
  useEffect(() => {
    fetchScheduledPosts();
  }, [fetchScheduledPosts, location.key]);

  // Listen for manual refetch events (from bottom bar tap)
  useEffect(() => {
    const handleRefetch = () => fetchScheduledPosts();
    window.addEventListener("refetch_calendar", handleRefetch);
    return () => window.removeEventListener("refetch_calendar", handleRefetch);
  }, [fetchScheduledPosts]);

  const handleCancelPost = (post) => {
    setPostToCancel(post);
  };

  const confirmCancelPost = async () => {
    if (!postToCancel) return;
    try {
      await axiosInstance.delete(`/scheduled-posts/${postToCancel._id}`);
      fetchScheduledPosts();
      toast.success(t('postCancelled'));
    } catch (err) {
      toast.error(t('failedToCancelPost'));
    }
    setPostToCancel(null);
  };

  const handlePostClick = (post) => {
    setSelectedPost(post);
  };

  const handlePostUpdate = () => {
    fetchScheduledPosts();
    setSelectedPost(null);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const getPostsForDate = (date) => {
    if (!date) return [];
    return scheduledPosts.filter(post => {
      const postDate = new Date(post.scheduledTime);
      return postDate.toDateString() === date.toDateString();
    });
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleDayClick = (date, posts) => {
    if (!date || posts.length === 0) return;
    // On mobile, show day detail sheet
    if (window.innerWidth <= 480) {
      setSelectedDay({ date, posts });
    }
  };

  return (
    <div className="calendar-view-content">
        <div className="calendar-header">
          <button className="calendar-nav" onClick={prevMonth}>
            ←
          </button>
          <span className="calendar-month">
            {currentMonth.toLocaleString("default", { month: "long", year: "numeric" })}
          </span>
          <button className="calendar-nav" onClick={nextMonth}>
            →
          </button>
        </div>

        <div className="calendar-grid">
          <div className="calendar-weekdays">
            {DAY_KEYS.map((day) => (
              <div key={day} className="calendar-weekday">{day}</div>
            ))}
          </div>
          <div className="calendar-days">
            {getDaysInMonth(currentMonth).map((date, idx) => {
              const posts = getPostsForDate(date);
              const isToday = date && date.toDateString() === new Date().toDateString();

              return (
                <div
                  key={idx}
                  className={`calendar-day ${!date ? "empty" : ""} ${isToday ? "today" : ""} ${posts.length > 0 ? "has-posts" : ""}`}
                  onClick={() => handleDayClick(date, posts)}
                >
                  {date && (
                    <>
                      <span className="calendar-date">{date.getDate()}</span>
                      {posts.length > 0 && (
                        <div className="calendar-posts">
                          {posts.slice(0, 2).map((post) => (
                            <div
                              key={post._id}
                              className={`calendar-post clickable status-${post.status || 'scheduled'}`}
                              onClick={() => handlePostClick(post)}
                            >
                              <span className="calendar-post-time">
                                {new Date(post.scheduledTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                              </span>
                            </div>
                          ))}
                          {posts.length > 2 && (
                            <span className="calendar-more">+{posts.length - 2}</span>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {scheduledPosts.length > 0 && (
          <div className="calendar-upcoming">
            <h4 className="calendar-upcoming-title">{t('upcomingClips')}</h4>
            <div className="calendar-upcoming-list">
              {scheduledPosts
                .filter((p) => p.status === "scheduled")
                .slice(0, 5)
                .map((post) => (
                  <div key={post._id} className="calendar-upcoming-item clickable">
                    <span className={`calendar-upcoming-status status-${post.status || 'scheduled'}`} />
                    <div
                      className="calendar-upcoming-info"
                      onClick={() => handlePostClick(post)}
                    >
                      <span className="calendar-upcoming-title-text">
                        {post.clipTitle || t('untitledClip')}
                      </span>
                      <span className="calendar-upcoming-time">
                        {new Date(post.scheduledTime).toLocaleString()}
                      </span>
                    </div>
                    <button
                      className="calendar-upcoming-cancel"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancelPost(post);
                      }}
                    >
                      {t('common:cancel')}
                    </button>
                  </div>
                ))}
            </div>
          </div>
        )}

        {scheduledPosts.length === 0 && (
          <div className="calendar-empty">
            <p>{t('noScheduledClipsThisMonth')}</p>
            <p className="calendar-empty-hint">
              {t('scheduleClipsHint')}
            </p>
          </div>
        )}

      {selectedPost && (
        <EditScheduledPostModal
          post={selectedPost}
          onClose={() => setSelectedPost(null)}
          onUpdate={handlePostUpdate}
        />
      )}

      <ConfirmModal
        isOpen={!!postToCancel}
        onClose={() => setPostToCancel(null)}
        onConfirm={confirmCancelPost}
        title={t('cancelScheduledPostConfirm')}
        itemName={postToCancel?.clipTitle || t('untitledClip')}
        message={t('cancelPostMessage')}
        confirmText={t('cancelPost')}
        cancelText={t('keepIt')}
        variant="warning"
      />

      {/* Mobile Day Detail Sheet */}
      {selectedDay && (
        <div className="calendar-day-sheet-overlay" onClick={() => setSelectedDay(null)}>
          <div className="calendar-day-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="calendar-day-sheet-header">
              <h4>
                {selectedDay.date.toLocaleDateString("default", {
                  weekday: "long",
                  month: "short",
                  day: "numeric",
                })}
              </h4>
              <button className="calendar-day-sheet-close" onClick={() => setSelectedDay(null)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="calendar-day-sheet-posts">
              {selectedDay.posts.map((post) => (
                <div
                  key={post._id}
                  className="calendar-day-sheet-post"
                  onClick={() => {
                    setSelectedDay(null);
                    handlePostClick(post);
                  }}
                >
                  <span className={`calendar-day-sheet-status status-${post.status || "scheduled"}`} />
                  <div className="calendar-day-sheet-info">
                    <span className="calendar-day-sheet-title">
                      {post.clipTitle || t('untitledClip')}
                    </span>
                    <span className="calendar-day-sheet-time">
                      {new Date(post.scheduledTime).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CalendarPage() {
  return (
    <div className="calendar-page">
      <Topbar />
      <div className="calendar-leftbar-wrapper">
        <Leftbar />
      </div>
      <div className="calendar-content">
        <CalendarView />
      </div>
    </div>
  );
}
